import API_CONFIG, { buildUrl, getBaseUrl, getPythonBaseUrl } from "../config/apiConfig";
import csrfService from "../utils/csrfService";


class ApiService {
  constructor() {
    this.config = API_CONFIG;
    // console.log("🚀 ApiService initialized. Method updateProjectStatus exists:", !!this.updateProjectStatus);
  }

  // Remove stale token on logout
  clearToken() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("last2FAVerified");
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    // Clear CSRF token on logout
    csrfService.clearToken();
  }

  // Generic request method with authentication and CSRF protection
  async request(url, options = {}) {
    try {
      const headers = { ...options.headers };
      if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
      }
      // If Content-Type is explicitly set to undefined/null, delete it
      if (headers["Content-Type"] === undefined || headers["Content-Type"] === null) {
        headers["Content-Type"] = undefined;
      }

      // 🔐 Attach Authorization header from localStorage if available.
      // Wichtig für Safari & andere Browser, die Cross-Site-Cookies blockieren.
      // Server akzeptiert auch das HTTP-only access_token Cookie als Fallback.
      if (!headers["Authorization"]) {
        const storedToken = localStorage.getItem("authToken");
        if (storedToken) {
          headers["Authorization"] = `Bearer ${storedToken}`;
        }
      }

      // 🔒 Add CSRF token for state-changing requests — nur wenn KEIN Bearer-Token
      // verwendet wird. Bearer-basierte Anfragen sind nicht CSRF-anfällig.
      const method = options.method?.toUpperCase() || 'GET';
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) && !headers["Authorization"]) {
        const csrfToken = await csrfService.ensureToken();
        headers['X-CSRF-Token'] = csrfToken;
      }

      // console.log("📤 Making request to:", url);
      const response = await fetch(url, {
        headers,
        credentials: "include",
        ...options,
      });

      // Get the raw response text first
      const responseText = await response.text();
      // console.log("📥 Raw response:", responseText);

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("❌ Failed to parse JSON:", parseError);
        console.error("📄 Response was:", responseText);
        throw new Error(
          `Server returned non-JSON response: ${responseText.substring(0, 100)}`
        );
      }

      // 🔒 Handle CSRF token expiration
      if (response.status === 403 && data.error === 'Invalid CSRF token') {
        // console.log('🔒 CSRF token expired, refreshing...');
        await csrfService.refreshToken();

        // Retry request with new token
        headers['X-CSRF-Token'] = csrfService.getToken();
        const retryResponse = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });

        const retryText = await retryResponse.text();
        const retryData = JSON.parse(retryText);

        if (!retryResponse.ok) {
          throw new Error(retryData.error || `HTTP error! status: ${retryResponse.status}`);
        }

        return retryData;
      }


      // Check if this is an expected authentication failure before throwing
      const isAuthEndpoint = url.includes('/api/auth/user') || url.includes('/userRoles');
      const isAuthFailure = !response.ok && (response.status === 401 || data.error === 'Token fehlt' || data.error?.includes('Authentication required'));

      if (isAuthEndpoint && isAuthFailure) {
        // Silently throw for expected auth failures - don't log
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      // Check if this is an expected authentication failure
      const errorMessage = error.message || '';
      const isAuthFailure = errorMessage.includes('401') || errorMessage.includes('Token fehlt') || errorMessage.includes('Unauthorized') || errorMessage.includes('Authentication required');
      const isAuthEndpoint = url.includes('/api/auth/user') || url.includes('/userRoles');

      // Only log unexpected errors
      if (!(isAuthFailure && isAuthEndpoint)) {
        console.error(`API request failed for ${url}:`, error);
      }
      throw error;
    }
  }

  // Specific API methods
  async loginUser(username, password) {
    // Use the configured URL instead of hardcoding
    const base = getBaseUrl("LOGIN");
    const endpoint = this.config.ENDPOINTS.LOGIN;
    const url = buildUrl(base, endpoint);

    // console.log("🔐 Login URL:", url);

    try {
      // Clear any stale token BEFORE login so the request isn't sent with an old Bearer
      this.clearToken();

      const data = await this.request(url, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      // Store the new JWT for subsequent requests (Safari-fallback wenn Cookie blockiert)
      if (data?.token) {
        localStorage.setItem("authToken", data.token);
      }

      return data;
    } catch (error) {
      console.error("❌ Login failed:", error);
      throw error;
    }
  }

  async checkAuth() {
    const base = getBaseUrl("USER");
    const endpoint = this.config.ENDPOINTS.USER;
    const url = buildUrl(base, endpoint);
    try {
      const userData = await this.request(url, {
        method: "GET",
      });
      return userData;
    } catch (error) {
      const errorMessage = error.message || '';
      const isExpectedAuthFailure = errorMessage.includes('Token fehlt') ||
                                     errorMessage.includes('401') ||
                                     errorMessage.includes('Authentication required');

      if (!isExpectedAuthFailure) {
        console.error("❌ Auth check failed:", error);
      }
      throw new Error("Not authenticated");
    }
  }

  async logoutUser(username) {
    try {
      const url = buildUrl(getBaseUrl("LOGOUT"), this.config.ENDPOINTS.LOGOUT);
      await this.request(url, {
        method: "POST",
        body: JSON.stringify({ username }),
      });
    } catch (err) {
      console.warn("⚠️ Node.js backend logout failed:", err);
    }

    try {
      const pythonLogoutUrl = `${getPythonBaseUrl()}/logout`;
      await fetch(pythonLogoutUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username }) // Some implementations might need this
      });
    } catch (err) {
      console.warn("⚠️ Python backend logout failed (ignoring):", err);
    }

    this.clearToken();
  }

  async loginAdmin(username, password) {
    const url = buildUrl(
      getBaseUrl("ADMIN_LOGIN"),
      this.config.ENDPOINTS.ADMIN_LOGIN
    );

    this.clearToken();

    const data = await this.request(url, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });

    if (data?.token) {
      localStorage.setItem("authToken", data.token);
    }
    return data;
  }

  async getUserRoles(username) {
    const base = getBaseUrl("USER_ROLES");
    const endpoint = this.config.ENDPOINTS.USER_ROLES;
    const url = buildUrl(base, endpoint);
    return this.request(url, {
      method: "GET",
    });
  }

  async createNewUser(userData) {
    const url = buildUrl(
      getBaseUrl("REGISTER"),
      this.config.ENDPOINTS.REGISTER
    );

    return this.request(url, {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async updateBillingAddress(username, billingAddress) {
    const url = buildUrl(
      getBaseUrl("BILLING_ADDRESS"),
      this.config.ENDPOINTS.BILLING_ADDRESS
    );

    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ username, billingAddress }),
    });
  }

  async uploadScan(formData) {
    const url = buildUrl(
      getBaseUrl("UPLOAD_SCAN"),
      this.config.ENDPOINTS.UPLOAD_SCAN
    );

    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Upload fehlgeschlagen");
    }

    return response.json();
  }

  async getProcessingStatus(folder) {
    const base = getBaseUrl("PROCESSING_STATUS");
    const endpoint = this.config.ENDPOINTS.PROCESSING_STATUS;
    const url = buildUrl(base, `${endpoint}?folder=${folder}`);

    // console.log("🔍 Checking processing status:", url);

    return this.request(url, { method: "GET" });
  }

  // In your ApiService class in api.js, add these methods:

  async getUploadProcessingStatus(folder) {
    const base = getBaseUrl("UPLOAD_PROCESSING_STATUS");
    const endpoint = this.config.ENDPOINTS.UPLOAD_PROCESSING_STATUS;
    const url = buildUrl(base, `${endpoint}/${folder}`);

    // console.log("🔍 Checking upload processing status:", url);

    return this.request(url, { method: "GET" });
  }

  async checkModelFiles(folder) {
    const base = getBaseUrl("CHECK_MODEL_FILES");
    const endpoint = this.config.ENDPOINTS.CHECK_MODEL_FILES;
    const url = buildUrl(base, `${endpoint}?folder=${folder}`);

    // console.log("🔍 Checking model files:", url);

    return this.request(url, { method: "GET" });
  }

  async downloadAttempt(folder) {
    const base = getBaseUrl("DOWNLOAD_ATTEMPT");
    const endpoint = this.config.ENDPOINTS.DOWNLOAD_ATTEMPT;
    const url = buildUrl(base, `${endpoint}?folder=${folder}`);

    // console.log("📥 Downloading attempt:", url);

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Download failed");
    }

    return response.blob();
  }

  // Add the missing getStatus method for backward compatibility
  async getStatus(folder) {
    return this.getProcessingStatus(folder);
  }
  async getPreviewImage(folder, imageName) {
    const base = getBaseUrl("PREVIEW_IMAGE");
    const endpoint = this.config.ENDPOINTS.PREVIEW_IMAGE;
    const url = buildUrl(
      base,
      `${endpoint}?folder=${folder}&image=${imageName}`
    );

    // console.log("🖼️ Downloading preview image:", url);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to load preview image");
    }

    return response.blob();
  }

  async sendApprovalDecision(folder, decision) {
    const base = getBaseUrl("APPROVAL_DECISION");
    const endpoint = this.config.ENDPOINTS.APPROVAL_DECISION;
    const url = buildUrl(base, endpoint);

    // console.log("📝 Sending approval decision:", url, { folder, decision });

    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ folder, decision }),
    });
  }


  async getPreviewFolderContent(folder) {
    const base = getBaseUrl("PREVIEW_FOLDER_CONTENT");
    const endpoint = this.config.ENDPOINTS.PREVIEW_FOLDER_CONTENT;
    const url = buildUrl(base, `${endpoint}?folder=${folder}`);

    // console.log("🖼️ Fetching preview folder content:", url);

    return this.request(url, { method: "GET" });
  }

  // Future-proofing: Method to update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  async getJobId(category, filename, size, forceNew = false) {
    const url = `${getBaseUrl()}/api/python-backend/get-job-id`;
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify({ category, filename, size, forceNew })
    });
  }

  async getUserJobs(page = 1, limit = 10) {
    const url = `${getBaseUrl()}/api/python-backend/jobs?page=${page}&limit=${limit}`;
    return this.request(url, { method: 'GET' });
  }

  // SECURITY: username is now taken from JWT token on backend, not from request
  async consumeDownload(jobId) {
    const url = `${getBaseUrl()}/api/payment/consume-download`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ jobId }),
    });
  }

  async getUserSubscriptions(username) {
    const url = `${getBaseUrl()}/api/payment/user-subscriptions/${encodeURIComponent(username)}`;
    return this.request(url, { method: "GET" });
  }

  async getUserInvoices(username) {
    const url = `${getBaseUrl()}/api/payment/invoices/${encodeURIComponent(username)}`;
    return this.request(url, { method: "GET" });
  }

  async getUserProjects(page = 1, username, limit = 10) {
    const url = `${getBaseUrl()}/api/user/projects/${page}`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ username, limit }),
    });
  }

  // Get active (in-progress) project for session restoration
  async getActiveProject() {
    const url = `${getBaseUrl()}/api/user/projects/active`;
    return this.request(url, { method: "GET" });
  }

  async uploadThumbnail(jobId, imageBlob) {
    const url = `${getBaseUrl()}/api/python-backend/projects/${jobId}/thumbnail`;
    const formData = new FormData();
    formData.append("image", imageBlob, "thumbnail.jpg");

    return this.request(url, {
      method: "POST",
      body: formData
      // Don't set Content-Type - browser will set it automatically with boundary
    });
  }

  // Save worker logs and progress for a project
  async saveWorkerLogs(jobId, logs, progress, progressStage) {
    const url = `${getBaseUrl()}/api/python-backend/projects/${jobId}/logs`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ logs, progress, progressStage }),
    });
  }

  // Get worker logs for a project (for session restoration)
  async getWorkerLogs(jobId) {
    const url = `${getBaseUrl()}/api/python-backend/projects/${jobId}/logs`;
    return this.request(url, { method: "GET" });
  }

  async saveResult(jobId, loggedInUser) {
    const url = `${getBaseUrl()}/api/upload/save-result`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ jobId, loggedInUser }),
    });
  }

  async deleteProject(projectId) {
    const url = `${getBaseUrl()}/api/user/projects/${projectId}`;
    return this.request(url, {
      method: "DELETE"
    });
  }

  async updateProjectStatus(jobId, status, previewUrl = null, pythonStatus = null, pythonJobId = null, category = null, cloudUrl = null) {
    if (!jobId) {
      console.warn("⚠️ updateProjectStatus called without jobId");
      return;
    }
    const url = `${getBaseUrl()}/api/python-backend/update-status`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ jobId, status, previewUrl, pythonStatus, pythonJobId, category, cloudUrl }),
    });
  }

  // Accept a project in "review" status, transitions to "ready" and sends email notification
  async acceptProject(jobId) {
    if (!jobId) {
      console.warn("⚠️ acceptProject called without jobId");
      return;
    }
    const url = `${getBaseUrl()}/api/python-backend/projects/${encodeURIComponent(jobId)}/accept`;
    return this.request(url, {
      method: "POST",
    });
  }

  async getProfile(username) {
    const url = `${getBaseUrl()}/api/profile/${encodeURIComponent(username)}`;
    return this.request(url, { method: "GET" });
  }

  async updateProfile(username, profileData) {
    const url = `${getBaseUrl()}/api/profile/${encodeURIComponent(username)}`;
    return this.request(url, {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  async changePassword(currentPassword, newPassword, confirmPassword) {
    const url = `${getBaseUrl()}/api/user/change-password`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });
  }

  async getProjectDetails(projectId) {
    const url = `${getBaseUrl()}/api/user/projects/${projectId}`;
    return this.request(url, { method: "GET" });
  }

  async closeConversation(conversationId) {
    const url = `${getBaseUrl()}/api/messages/conversation/${conversationId}/close`;
    return this.request(url, { method: "PUT" });
  }

  async checkSupportOnline() {
    const url = `${getBaseUrl()}/api/messages/support-online`;
    return this.request(url, { method: "GET" });
  }

  async updateSupportStatus(isOnline) {
    const url = `${getBaseUrl()}/api/messages/support-status`;
    return this.request(url, {
      method: "PUT",
      body: JSON.stringify({ isOnline })
    });
  }

  async syncProjectStatuses() {
    const url = `${getBaseUrl()}/api/user/projects/sync-status`;
    return this.request(url, { method: "POST" });
  }

  // ====================== NEXTCLOUD ======================

  /**
   * Get Nextcloud connection status
   */
  async getNextcloudStatus() {
    const url = `${getBaseUrl()}/api/nextcloud/status`;
    return this.request(url, { method: "GET" });
  }

  /**
   * Initialize Nextcloud Login Flow v2
   * Returns loginUrl that user needs to open in browser
   */
  async initNextcloudLogin() {
    const url = `${getBaseUrl()}/api/nextcloud/init-login`;
    return this.request(url, { method: "POST" });
  }

  /**
   * Poll for Nextcloud login completion
   * @param {string} localToken - Token from initNextcloudLogin
   */
  async pollNextcloudLogin(localToken) {
    const url = `${getBaseUrl()}/api/nextcloud/poll-login`;
    return this.request(url, {
      method: "POST",
      body: JSON.stringify({ localToken }),
    });
  }

  /**
   * Disconnect Nextcloud
   */
  async disconnectNextcloud() {
    const url = `${getBaseUrl()}/api/nextcloud/disconnect`;
    return this.request(url, { method: "DELETE" });
  }

  /**
   * Test Nextcloud connection
   */
  async testNextcloudConnection() {
    const url = `${getBaseUrl()}/api/nextcloud/test-connection`;
    return this.request(url, { method: "GET" });
  }

  // --- Scan Settings (Admin) ---

  async getScanSettings() {
    const url = `${getBaseUrl()}/api/admin/scan-settings`;
    return this.request(url, { method: "GET" });
  }

  async updateScanSettings(settings) {
    const url = `${getBaseUrl()}/api/admin/scan-settings`;
    return this.request(url, {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  // --- Scan Attempts (User) ---

  async getScanAttemptsRemaining() {
    const url = `${getBaseUrl()}/api/python-backend/scan-attempts/remaining`;
    return this.request(url, { method: "GET" });
  }

  async syncProjectToCloud(jobId) {
    const url = `${getBaseUrl()}/api/python-backend/projects/${jobId}/sync-to-cloud`;
    return this.request(url, { method: "POST" });
  }
}

// Create singleton instance
export const apiService = new ApiService();
export default apiService;

// Backward compatibility - named exports
export const loginUser = (username, password) =>
  apiService.loginUser(username, password);
export const logoutUser = (username) => apiService.logoutUser(username);
export const loginAdmin = (username, password) =>
  apiService.loginAdmin(username, password);
export const createNewUser = (userData) => apiService.createNewUser(userData);
export const updateBillingAddress = (username, billingAddress) =>
  apiService.updateBillingAddress(username, billingAddress);
export const uploadScan = (formData) =>
  apiService.uploadScan(formData);
export const getPreviewFolderContent = (folder) =>
  apiService.getPreviewFolderContent(folder);
export const getPreviewImage = (folder, imageName) =>
  apiService.getPreviewImage(folder, imageName);
export const sendApprovalDecision = (folder, decision) =>
  apiService.sendApprovalDecision(folder, decision);
export const getUploadProcessingStatus = (folder) =>
  apiService.getUploadProcessingStatus(folder);
export const checkModelFiles = (folder) => apiService.checkModelFiles(folder);
export const downloadAttempt = (folder) => apiService.downloadAttempt(folder);
export const getJobId = (category, filename, size, forceNew) => apiService.getJobId(category, filename, size, forceNew);
export const getUserJobs = (page, limit) => apiService.getUserJobs(page, limit);
export const consumeDownload = (jobId) => apiService.consumeDownload(jobId);
export const getUserSubscriptions = (username) => apiService.getUserSubscriptions(username);
export const getUserInvoices = (username) => apiService.getUserInvoices(username);
export const getUserProjects = (page, username, limit) => apiService.getUserProjects(page, username, limit);
export const getActiveProject = () => apiService.getActiveProject();
export const updateProjectStatus = (jobId, status, previewUrl) => apiService.updateProjectStatus(jobId, status, previewUrl);
export const acceptProject = (jobId) => apiService.acceptProject(jobId);
export const getProfile = (username) => apiService.getProfile(username);
export const updateProfile = (username, profileData) => apiService.updateProfile(username, profileData);
export const getProjectDetails = (projectId) => apiService.getProjectDetails(projectId);
export const closeConversation = (conversationId) => apiService.closeConversation(conversationId);
export const checkSupportOnline = () => apiService.checkSupportOnline();
export const updateSupportStatus = (isOnline) => apiService.updateSupportStatus(isOnline);
